import { useEffect, useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import {
  X, Trash2, ShoppingBasket, Loader2, ChevronLeft, CheckCircle2,
  ImageIcon, Smartphone, Banknote, Landmark, ClipboardCopy,
} from "lucide-react";
import { useCart } from "./CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const METHOD_ICONS = {
  mbway: Smartphone,
  transferencia: Landmark,
  dinheiro_entrega: Banknote,
};

function resolveImageUrl(p) {
  if (!p) return null;
  if (p.startsWith("http://") || p.startsWith("https://")) return p;
  return `${process.env.REACT_APP_BACKEND_URL}${p}`;
}

export default function CartDrawer() {
  const { items, updateQty, remove, clear, total, open, setOpen } = useCart();
  const [view, setView] = useState("cart"); // cart | checkout | success
  const [slots, setSlots] = useState([]);
  const [paymentSettings, setPaymentSettings] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    postal_code: "",
    locality: "",
    delivery_date: "",
    delivery_period: "tarde",
    payment_method: "mbway",
    customer_notes: "",
  });

  useEffect(() => {
    if (!open) return;
    // Compute valid Tuesday-only delivery dates client-side with 16:00 cutoff
    const now = new Date();
    const isoOffset = now.getTimezoneOffset(); // rough; server enforces truth
    const cutoffHour = now.getHours();
    const slots = [];
    for (let i = 1; i <= 42; i++) {
      const d = new Date(now);
      d.setDate(now.getDate() + i);
      const wd = d.getDay();
      if (wd !== 2) continue;
      // Block tomorrow if it's already 16:00 or later today
      if (i === 1 && cutoffHour >= 16) continue;
      const iso = d.toISOString().slice(0, 10);
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      slots.push({ date: iso, label: `Terça-feira ${dd}/${mm}/${d.getFullYear()}` });
    }
    void isoOffset;
    setSlots(slots);
    if (!form.delivery_date && slots.length) {
      setForm((f) => ({ ...f, delivery_date: slots[0].date }));
    }
    // Load payment instructions (only once)
    if (!paymentSettings) {
      axios
        .get(`${API}/settings/payment`)
        .then((r) => setPaymentSettings(r.data))
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) {
      // small delay to let the close animation play before resetting view
      const t = setTimeout(() => setView("cart"), 250);
      return () => clearTimeout(t);
    }
  }, [open]);

  const submit = async () => {
    if (items.length === 0) return;
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        address: form.address.trim(),
        postal_code: form.postal_code.trim() || null,
        locality: form.locality.trim(),
        delivery_date: form.delivery_date,
        delivery_period: form.delivery_period,
        payment_method: form.payment_method,
        customer_notes: form.customer_notes.trim() || null,
        items: items.map((i) => ({
          product_id: i.product_id,
          name: i.name,
          quantity: i.quantity,
          unit: i.unit,
          unit_price: i.unit_price,
          total: Number((i.unit_price * i.quantity).toFixed(2)),
        })),
      };
      const { data } = await axios.post(`${API}/orders`, payload);
      setConfirmation(data);
      setView("success");
      clear();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erro ao enviar encomenda");
    } finally {
      setSubmitting(false);
    }
  };

  const minOrder = paymentSettings?.min_order_amount || 0;
  const belowMin = minOrder > 0 && total > 0 && total < minOrder;

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm"
            onClick={() => setOpen(false)}
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="fixed right-0 top-0 bottom-0 z-50 w-full sm:max-w-md bg-white shadow-2xl flex flex-col"
            data-testid="cart-drawer"
          >
            <header className="px-5 py-4 border-b border-[#D4C4A8]/40 flex items-center gap-3">
              {view === "checkout" && (
                <button
                  onClick={() => setView("cart")}
                  className="p-1 rounded hover:bg-[#F3F1EB]"
                  aria-label="Voltar"
                >
                  <ChevronLeft size={20} />
                </button>
              )}
              <h2 className="text-lg font-semibold text-[#2C2C2C]">
                {view === "cart" && "O seu cesto"}
                {view === "checkout" && "Finalizar encomenda"}
                {view === "success" && "Encomenda recebida"}
              </h2>
              <button
                onClick={() => setOpen(false)}
                data-testid="cart-close"
                className="ml-auto p-1 rounded hover:bg-[#F3F1EB]"
              >
                <X size={20} />
              </button>
            </header>

            <div className="flex-1 overflow-y-auto">
              {view === "cart" && (
                <CartItems items={items} updateQty={updateQty} remove={remove} />
              )}
              {view === "checkout" && (
                <CheckoutForm
                  form={form}
                  setForm={setForm}
                  slots={slots}
                  items={items}
                  total={total}
                  paymentSettings={paymentSettings}
                />
              )}
              {view === "success" && confirmation && (
                <SuccessView order={confirmation} paymentSettings={paymentSettings} />
              )}
            </div>

            <footer className="px-5 py-4 border-t border-[#D4C4A8]/40 bg-[#F3F1EB]/40">
              {view === "cart" && (
                <>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm text-[#2C2C2C]/60">Total</span>
                    <span className="text-2xl font-semibold text-[#3A4A38]">
                      {Number(total).toFixed(2)}€
                    </span>
                  </div>
                  {belowMin && (
                    <p
                      data-testid="cart-min-warning"
                      className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-2"
                    >
                      Valor mínimo de encomenda: <strong>{minOrder.toFixed(2)}€</strong>. Faltam{" "}
                      <strong>{(minOrder - total).toFixed(2)}€</strong> para finalizar.
                    </p>
                  )}
                  <button
                    onClick={() => setView("checkout")}
                    disabled={items.length === 0 || belowMin}
                    data-testid="cart-checkout-btn"
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-[#3A4A38] text-white font-medium hover:bg-[#2C3A2A] transition-colors disabled:opacity-50"
                  >
                    Finalizar encomenda
                  </button>
                </>
              )}
              {view === "checkout" && (
                <button
                  onClick={submit}
                  disabled={
                    submitting ||
                    !form.name ||
                    !form.email ||
                    !form.phone ||
                    !form.address ||
                    !form.locality ||
                    !form.delivery_date
                  }
                  data-testid="checkout-submit-btn"
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-[#3A4A38] text-white font-medium hover:bg-[#2C3A2A] transition-colors disabled:opacity-50"
                >
                  {submitting ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <CheckCircle2 size={16} />
                  )}
                  {submitting ? "A enviar..." : `Confirmar (${Number(total).toFixed(2)}€)`}
                </button>
              )}
              {view === "success" && (
                <button
                  onClick={() => setOpen(false)}
                  className="w-full px-4 py-3 rounded-full bg-[#3A4A38] text-white font-medium hover:bg-[#2C3A2A] transition-colors"
                >
                  Fechar
                </button>
              )}
            </footer>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}

function CartItems({ items, updateQty, remove }) {
  if (items.length === 0) {
    return (
      <div className="p-10 text-center">
        <ShoppingBasket size={40} className="mx-auto text-[#2C2C2C]/20 mb-4" />
        <p className="text-sm text-[#2C2C2C]/60">O seu cesto está vazio.</p>
      </div>
    );
  }
  return (
    <ul className="divide-y divide-[#D4C4A8]/30">
      {items.map((i) => {
        const imgUrl = resolveImageUrl(i.image_url);
        return (
          <li key={i.product_id} className="p-4 flex gap-3" data-testid={`cart-item-${i.product_id}`}>
            <div className="w-16 h-16 rounded-lg overflow-hidden bg-[#F3F1EB] flex-shrink-0">
              {imgUrl ? (
                <img src={imgUrl} alt={i.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[#2C2C2C]/20">
                  <ImageIcon size={20} />
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-semibold text-[#2C2C2C] truncate">{i.name}</h4>
              <p className="text-xs text-[#2C2C2C]/60">
                {Number(i.unit_price).toFixed(2)}€ / {i.unit}
              </p>
              <QuantityRow item={i} updateQty={updateQty} onRemove={() => remove(i.product_id)} />
            </div>
            <div className="text-sm font-medium text-[#3A4A38] whitespace-nowrap">
              {Number(i.unit_price * i.quantity).toFixed(2)}€
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function QuantityRow({ item, updateQty, onRemove }) {
  const isKg = item.unit === "kg" || item.unit === "litro";
  const step = isKg ? 0.5 : 1;
  const formatQty = (n) => (isKg ? Number(n).toString().replace(".", ",") : String(Math.round(n)));
  const [buffer, setBuffer] = useState(formatQty(item.quantity));

  // Keep buffer in sync when quantity changes from outside (+/- buttons)
  useEffect(() => {
    setBuffer(formatQty(item.quantity));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.quantity, isKg]);

  const commit = () => {
    const raw = buffer.replace(",", ".").trim();
    const n = Number(raw);
    if (!Number.isFinite(n) || n <= 0) {
      // invalid or 0 → revert to minimum, do NOT remove
      updateQty(item.product_id, step);
      setBuffer(formatQty(step));
      return;
    }
    updateQty(item.product_id, isKg ? n : Math.round(n));
  };

  const onChange = (e) => {
    const v = e.target.value;
    // Allow digits, one comma or dot (only for kg), and empty while editing
    if (isKg) {
      if (/^[0-9]*([.,][0-9]*)?$/.test(v)) setBuffer(v);
    } else if (/^[0-9]*$/.test(v)) {
      setBuffer(v);
    }
  };

  const inc = () => updateQty(item.product_id, item.quantity + step);
  const dec = () => updateQty(item.product_id, item.quantity - step);

  return (
    <div className="flex items-center gap-2 mt-2">
      <button
        type="button"
        onClick={dec}
        className="w-7 h-7 rounded-full border border-[#D4C4A8] hover:bg-[#F3F1EB] text-sm"
        aria-label="Diminuir"
        data-testid={`cart-qty-dec-${item.product_id}`}
      >
        −
      </button>
      <input
        type="text"
        inputMode={isKg ? "decimal" : "numeric"}
        value={buffer}
        onChange={onChange}
        onBlur={commit}
        onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
        aria-label="Quantidade"
        data-testid={`cart-qty-input-${item.product_id}`}
        className="w-16 px-2 py-1 text-center text-sm rounded border border-[#D4C4A8] bg-white [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
      />
      <span className="text-xs text-[#2C2C2C]/50 select-none">{item.unit}</span>
      <button
        type="button"
        onClick={inc}
        className="w-7 h-7 rounded-full border border-[#D4C4A8] hover:bg-[#F3F1EB] text-sm"
        aria-label="Aumentar"
        data-testid={`cart-qty-inc-${item.product_id}`}
      >
        +
      </button>
      <button
        type="button"
        onClick={onRemove}
        className="ml-auto p-1.5 rounded text-red-600 hover:bg-red-50"
        aria-label="Remover"
        data-testid={`cart-remove-${item.product_id}`}
      >
        <Trash2 size={14} />
      </button>
    </div>
  );
}

function CheckoutForm({ form, setForm, slots, items, total, paymentSettings }) {
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const methods = paymentSettings?.methods || [];
  const activeMethod = methods.find((m) => m.key === form.payment_method) || methods[0];

  // Auto-pick first valid method when settings load
  useEffect(() => {
    if (methods.length && !methods.some((m) => m.key === form.payment_method)) {
      setForm((f) => ({ ...f, payment_method: methods[0].key }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentSettings]);

  const copyMbway = () => {
    navigator.clipboard.writeText(paymentSettings?.mbway_number || "");
    toast.success("Número copiado");
  };
  const copyIban = () => {
    navigator.clipboard.writeText(paymentSettings?.iban || "");
    toast.success("IBAN copiado");
  };

  return (
    <div className="p-5 space-y-4">
      <Section title="Os seus dados">
        <Field label="Nome completo *">
          <input
            type="text"
            value={form.name}
            onChange={set("name")}
            required
            data-testid="checkout-name"
            className="w-full px-3 py-2 rounded border border-[#D4C4A8] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#3A4A38]"
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Email *">
            <input
              type="email"
              value={form.email}
              onChange={set("email")}
              required
              data-testid="checkout-email"
              className="w-full px-3 py-2 rounded border border-[#D4C4A8] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#3A4A38]"
            />
          </Field>
          <Field label="Telefone *">
            <input
              type="tel"
              value={form.phone}
              onChange={set("phone")}
              required
              data-testid="checkout-phone"
              className="w-full px-3 py-2 rounded border border-[#D4C4A8] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#3A4A38]"
            />
          </Field>
        </div>
      </Section>

      <Section title="Morada de entrega">
        <Field label="Rua e número *">
          <input
            type="text"
            value={form.address}
            onChange={set("address")}
            required
            data-testid="checkout-address"
            className="w-full px-3 py-2 rounded border border-[#D4C4A8] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#3A4A38]"
          />
        </Field>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Código postal" className="col-span-1">
            <input
              type="text"
              value={form.postal_code}
              onChange={set("postal_code")}
              placeholder="0000-000"
              className="w-full px-3 py-2 rounded border border-[#D4C4A8] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#3A4A38]"
            />
          </Field>
          <Field label="Localidade *" className="col-span-2">
            <select
              value={form.locality}
              onChange={set("locality")}
              required
              data-testid="checkout-locality"
              className="w-full px-3 py-2 rounded border border-[#D4C4A8] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#3A4A38]"
            >
              <option value="">Selecione...</option>
              <option value="Braga">Braga</option>
              <option value="Vila Verde">Vila Verde</option>
            </select>
          </Field>
        </div>
      </Section>

      <Section title="Dia de entrega">
        <Field label="Data preferencial *">
          <select
            value={form.delivery_date}
            onChange={set("delivery_date")}
            required
            data-testid="checkout-delivery-date"
            className="w-full px-3 py-2 rounded border border-[#D4C4A8] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#3A4A38]"
          >
            {slots.map((s) => (
              <option key={s.date} value={s.date}>
                {s.label}
              </option>
            ))}
          </select>
        </Field>
        <p className="text-[11px] text-[#2C2C2C]/50 mt-1">
          Entrega: período da tarde. Entregamos todas as terças-feiras à tarde na região
          de Braga e Vila Verde. Encomendas para o dia seguinte só são aceites até às 16:00.
        </p>
      </Section>

      <Section title="Método de pagamento">
        <div className="space-y-2" data-testid="checkout-payment-methods">
          {methods.map((m) => {
            const Icon = METHOD_ICONS[m.key] || Smartphone;
            const active = form.payment_method === m.key;
            return (
              <button
                type="button"
                key={m.key}
                onClick={() => setForm({ ...form, payment_method: m.key })}
                data-testid={`payment-${m.key}`}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border text-left transition-colors ${
                  active
                    ? "border-[#3A4A38] bg-[#3A4A38]/5 ring-2 ring-[#3A4A38]/20"
                    : "border-[#D4C4A8] bg-white hover:bg-[#F3F1EB]/40"
                }`}
              >
                <Icon size={18} className={active ? "text-[#3A4A38]" : "text-[#2C2C2C]/60"} />
                <span className={`text-sm font-medium ${active ? "text-[#3A4A38]" : "text-[#2C2C2C]"}`}>
                  {m.label}
                </span>
                {active && <CheckCircle2 size={16} className="ml-auto text-[#3A4A38]" />}
              </button>
            );
          })}
        </div>
        {activeMethod && (
          <div className="mt-3 rounded-lg bg-[#F3F1EB]/60 border border-[#D4C4A8]/40 p-3">
            <p className="text-xs text-[#2C2C2C]/80 whitespace-pre-line leading-relaxed">
              {activeMethod.instructions}
            </p>
            {form.payment_method === "mbway" && paymentSettings?.mbway_number && (
              <button
                type="button"
                onClick={copyMbway}
                data-testid="copy-mbway"
                className="mt-2 inline-flex items-center gap-1.5 text-xs text-[#3A4A38] hover:underline"
              >
                <ClipboardCopy size={12} />
                Copiar {paymentSettings.mbway_number}
              </button>
            )}
            {form.payment_method === "transferencia" && paymentSettings?.iban && (
              <button
                type="button"
                onClick={copyIban}
                data-testid="copy-iban"
                className="mt-2 inline-flex items-center gap-1.5 text-xs text-[#3A4A38] hover:underline"
              >
                <ClipboardCopy size={12} />
                Copiar IBAN
              </button>
            )}
          </div>
        )}
      </Section>

      <Section title="Observações (opcional)">
        <textarea
          value={form.customer_notes}
          onChange={set("customer_notes")}
          rows={3}
          placeholder="Indicações para o entregador..."
          className="w-full px-3 py-2 rounded border border-[#D4C4A8] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#3A4A38]"
        />
      </Section>

      <Section title="Resumo">
        <ul className="text-xs text-[#2C2C2C]/80 space-y-1 mb-2">
          {items.map((i) => (
            <li key={i.product_id} className="flex justify-between">
              <span>
                {i.quantity} {i.unit} · {i.name}
              </span>
              <span>{Number(i.unit_price * i.quantity).toFixed(2)}€</span>
            </li>
          ))}
        </ul>
        <div className="flex justify-between font-semibold text-[#3A4A38] pt-2 border-t border-[#D4C4A8]/40">
          <span>Total</span>
          <span>{Number(total).toFixed(2)}€</span>
        </div>
      </Section>
    </div>
  );
}

function SuccessView({ order, paymentSettings }) {
  const methods = paymentSettings?.methods || [];
  const method = methods.find((m) => m.key === order.payment_method);
  return (
    <div className="p-6 text-center">
      <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-green-100 text-green-700 mb-4">
        <CheckCircle2 size={28} />
      </div>
      <h3 className="text-xl font-semibold text-[#2C2C2C]">Obrigado!</h3>
      <p className="text-sm text-[#2C2C2C]/70 mt-2">
        Recebemos a sua encomenda{" "}
        <span className="font-mono font-semibold">{order.order_number}</span>.
      </p>
      <div className="mt-5 bg-[#F3F1EB]/60 rounded-xl p-4 text-left text-sm space-y-1.5">
        <p>
          <strong>Entrega:</strong> {order.delivery_date} · período da tarde
        </p>
        <p>
          <strong>Total a pagar:</strong>{" "}
          <span className="text-[#3A4A38] font-semibold">{Number(order.total).toFixed(2)}€</span>
        </p>
        <p>
          <strong>Método:</strong> {method?.label || order.payment_method}
        </p>
      </div>
      {method && (
        <div className="mt-3 rounded-xl bg-amber-50 border border-amber-200 p-4 text-left">
          <p className="text-xs font-semibold text-amber-900 uppercase tracking-wider mb-2">
            Como pagar
          </p>
          <p className="text-xs text-amber-900 whitespace-pre-line leading-relaxed">
            {method.instructions}
          </p>
          <p className="text-[11px] text-amber-800/80 mt-2">
            Indique <strong>{order.order_number}</strong> na descrição do pagamento.
          </p>
        </div>
      )}
      <p className="text-xs text-[#2C2C2C]/60 mt-4">
        Vamos confirmar a encomenda por telefone ou email assim que recebermos o pagamento.
      </p>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h3 className="text-xs font-semibold uppercase tracking-wider text-[#2C2C2C]/60 mb-2">{title}</h3>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-xs font-medium text-[#2C2C2C]/70 mb-1">{label}</span>
      {children}
    </label>
  );
}
